// src/pages/AnalyticsPage.tsx
import { useState, useEffect, useCallback } from 'react';
import api from '@/utils/api';
import Card from '@shared/Card';
import Button from '@shared/Button';
import LoadingSpinner from '@shared/LoadingSpinner';
import { toast } from 'react-hot-toast';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, Cell
} from 'recharts';
import {RefreshCcw, Download, Info, Filter} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import ToggleSwitch from '@shared/ToggleSwitch';
import { Modal } from '@shared/Modal';
import EmptyState from '@shared/EmptyState';
import { BarChart as BarChartIcon } from 'lucide-react';
import ResponsiveTable from '@shared/ResponsiveTable';

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  unit?: string;
}

interface SparklineData {
  value: number;
}

interface OverviewCardProps {
  title: string;
  value: string | number;
  change?: number | null;
  unit?: string;
  sparklineData?: SparklineData[];
  sparklineColor?: string;
  children?: React.ReactNode;
}

// Custom Tooltip for Charts
const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label, unit = '' }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-4 rounded-lg shadow-lg border border-gray-200">
        <p className="font-medium text-gray-900 mb-1">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={`item-${index}`} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: <span className="font-semibold">{entry.value}{unit}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Overview Card Component
const OverviewCard: React.FC<OverviewCardProps> = ({ title, value, change, unit = '', sparklineData, sparklineColor, children }) => {
  const isPositive = change !== undefined && change !== null && change >= 0;
  const changeColor = isPositive ? 'text-green-500' : 'text-red-500';

  return (
    <Card className="flex flex-col justify-between h-48">
      <div>
        <h3 className="text-sm font-medium text-gray-500">{title}</h3>
        <div className="mt-1 flex items-end justify-between">
          <p className="text-3xl font-bold text-gray-900">{value}{unit}</p>
          {change !== null && change !== undefined && (
            <span className={`inline-flex items-center text-sm font-semibold ${changeColor}`}>
              {isPositive ? '▲' : '▼'} {Math.abs(change).toFixed(1)}%
            </span>
          )}
        </div>
      </div>
      {sparklineData && sparklineData.length > 0 && (
        <ResponsiveContainer width="100%" height={80}>
          <LineChart data={sparklineData}>
            <Line
              type="monotone"
              dataKey="value"
              stroke={sparklineColor}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      )}
      {children}
    </Card>
  );
};

interface ExportOptions {
  period: string;
  metrics: string[];
  format: string;
}

// Analytics Page Component
const AnalyticsPage: React.FC = () => {
  // const { user } = useAuth();
  const [period, setPeriod] = useState('30d');
  const [overview, setOverview] = useState<any>(null);
  const [queriesOverTime, setQueriesOverTime] = useState<any[]>([]);
  const [topDocuments, setTopDocuments] = useState<any[]>([]);
  const [commonQueries, setCommonQueries] = useState<any[]>([]);
  const [userActivity, setUserActivity] = useState<any>(null);
  const [performance, setPerformance] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(false);
  // const [lastRefreshed, setLastRefreshed] = useState(Date.now());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    period: '30d',
    metrics: [],
    format: 'csv',
  });
  const [exportLoading, setExportLoading] = useState(false);

  const fetchAnalyticsData = useCallback(async () => {
    setLoading(true);
    try {
      // BEGIN: MODIFIED CODE
      // Corrected destructuring to include userActivityData and ensure all promises are awaited
      const [overviewData, queriesData, topDocsData, commonQueriesData, userActivityData, performanceData] = await Promise.all([
        api.getAnalyticsOverview(period),
        api.getQueriesOverTime(period, period === '7d' ? 'day' : 'day'), // Default to day interval for now
        api.getTopDocuments(period, 10),
        api.getCommonQueries(period, 10),
        api.getUserActivity(period), // This now correctly maps to userActivityData
        api.getPerformance(period),
      ]);
      // END: MODIFIED CODE

      setOverview(overviewData);
      // console.log(overviewData);
      setQueriesOverTime(
        queriesData.data.map((item: any) => ({
          ...item,
          timestamp: item.timestamp,
          successRate: item.queries > 0 ? ((item.successful / item.queries) * 100).toFixed(1) : 0,
        }))
      );
      setTopDocuments(topDocsData.documents);
      setCommonQueries(commonQueriesData.queries);
      setPerformance(performanceData);
      // BEGIN: MODIFIED CODE
      // Corrected assignment of userActivityData to the userActivity state
      setUserActivity(userActivityData);
      // END: MODIFIED CODE
      // setLastRefreshed(Date.now());
    } catch (error: any) {
      toast.error(`Failed to load analytics: ${error?.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (autoRefresh) {
      interval = setInterval(() => {
        if (!document.hidden) {
          fetchAnalyticsData();
        }
      }, 60000); // Refresh every 60 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, fetchAnalyticsData]);



  let formattedAvgResponseTime = 'N/A';
  if (overview && overview.avgResponseTime !== undefined && overview.avgResponseTime !== null) {
    const responseTimeMs = parseFloat(overview.avgResponseTime);
    if (!isNaN(responseTimeMs)) {
      formattedAvgResponseTime = responseTimeMs < 1000 ? `${Math.round(responseTimeMs)}ms`: `${(responseTimeMs / 1000).toFixed(1)}s`;
    }
  }

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const blob = await api.exportAnalytics(exportOptions.period, exportOptions.metrics, exportOptions.format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `smartdocs-analytics-${exportOptions.period}-${format(new Date(), 'yyyy-MM-dd')}.${exportOptions.format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('Analytics data exported successfully!');
      setShowExportModal(false);
    } catch (error: any) {
      toast.error(`Failed to export data: ${error?.message || 'Unknown error'}`);
    } finally {
      setExportLoading(false);
    }
  };

  if (loading && !overview) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  // ADDED: Column definitions for ResponsiveTable
  const topDocumentsColumns = [
    { key: 'documentName', header: 'Document Name' },
    { key: 'queryCount', header: 'Queries', render: (value: any) => <span className="text-right font-medium">{value}</span> },
    {
      key: 'successRate',
      header: 'Success Rate',
      render: (value: any) => (
        <span className={`font-medium ${value >= 80 ? 'text-green-600' : (value >= 50 ? 'text-yellow-600' : 'text-red-600')}`}>
          {value.toFixed(1)}%
        </span>
      ),
    },
  ];

  const commonQueriesColumns = [
    { key: 'query', header: 'Query Text' },
    { key: 'count', header: 'Count', render: (value: any) => <span className="text-right font-medium">{value}</span> },
    {
      key: 'successRate',
      header: 'Success Rate',
      render: (value: any) => (
        <span className={`font-medium ${value >= 80 ? 'text-green-600' : (value >= 50 ? 'text-yellow-600' : 'text-red-600')}`}>
          {value.toFixed(1)}%
        </span>
      ),
    },
  ];

  const topUsersColumns = [
    { key: 'userName', header: 'User' },
    { key: 'queryCount', header: 'Queries', render: (value: any) => <span className="text-right font-medium">{value}</span> },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6"> {/* MODIFIED: Added flex-col for mobile, md:flex-row for desktop */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4 md:mb-0">Analytics</h1> {/* MODIFIED: Added mb-4 for mobile spacing */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center space-y-4 md:space-y-1.5 md:space-x-4"> {/* MODIFIED: Added flex-col for mobile, space-y for vertical spacing, md:flex-row and md:space-x for desktop */}
          {/* BEGIN: MODIFIED CODE - Added md:order-last to buttons wrapper and md:order-first to controls wrapper */}
          <div className="flex space-x-4 md:order-last"> {/* Wrapper for buttons, now last on desktop */}
            <Button variant="secondary" onClick={fetchAnalyticsData} loading={loading} className="flex-1 flex items-center gap-2"> {/* MODIFIED: Added flex-1 for full width on mobile, flex items-center gap-2 for icon/text alignment */}
              <RefreshCcw className="w-4 h-4" /> <span>Refresh</span>
            </Button>
            <Button onClick={() => setShowExportModal(true)} className="flex-1 flex items-center gap-2"> {/* MODIFIED: Added flex-1 for full width on mobile, flex items-center gap-2 for icon/text alignment */}
              <Download className="w-4 h-4" /> <span>Export</span>
            </Button>
          </div>
          <div className="flex items-center px-0 md:px-3 space-x-4 md:order-first"> {/* Wrapper for select and toggle, now first on desktop */}
            <div className="flex gap-4 w-full md:w-auto justify-end">
              <ToggleSwitch
                id="auto-refresh"
                label="Auto-refresh"
                checked={autoRefresh}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutoRefresh(e.target.checked)}
              />
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <select
                  className="appearance-none border border-gray-300 rounded-lg pl-10 pr-8 py-2 focus:ring-2 focus:ring-primary-500 bg-white"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value)}
                >
                  <option value="7d">Last 7 days</option>
                  <option value="30d">Last 30 days</option>
                  <option value="90d">Last 90 days</option>
                  <option value="all">All time</option>
                </select>
                <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                  <svg className="fill-current h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
                    <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
                  </svg>
                </div>
              </div>

            </div>
          </div>
          {/* END: MODIFIED CODE */}
        </div>
      </div>

      {/* Key Metrics Cards */}
      {overview ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <OverviewCard
            title="Total Queries"
            value={overview.totalQueries.toLocaleString()}
            change={overview.totalQueryChange}
            sparklineData={queriesOverTime.map(item => ({ value: item.queries }))}
            sparklineColor="#3b82f6"
          />
          <OverviewCard
            title="Avg Response Time"
            value={formattedAvgResponseTime}
            change={overview.avgResponseTimeChange}
            sparklineData={queriesOverTime.map(item => ({ value: item.avgResponseTime }))}
            sparklineColor="#6366f1"
          />
          <OverviewCard
            title="Success Rate"
            value={overview.successRate.toFixed(1)}
            unit="%"
            change={overview.successRateChange}
          >
            <div className="flex justify-center items-center h-full pt-4">
              <ResponsiveContainer width="80%" height="80%">
                {/* Recharts PieChart or similar can be used for success rate donut chart */}
                {/* For simplicity, using a BarChart as a visual placeholder, but a PieChart would be more appropriate for a donut */}
                <BarChart data={[{ name: 'Success', value: overview.successRate }, { name: 'Failed', value: 100 - overview.successRate }]} layout="vertical">
                  <Bar dataKey="value" fill={overview.successRate >= 80 ? '#10b981' : (overview.successRate >= 50 ? '#f59e0b' : '#ef4444')} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </OverviewCard>
          <OverviewCard
            title="Active Users"
            value={overview.activeUsers.toLocaleString()}
            change={overview.activeUsersChange}
            sparklineData={userActivity?.userGrowth?.map((item: any) => ({ value: item.count }))} // Assuming userGrowth is available here
            sparklineColor="#10b981"
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="h-48 animate-pulse bg-gray-100 flex flex-col justify-between">
              <div className="h-4 w-3/4 bg-gray-200 rounded mb-2"></div>
              <div className="h-8 w-1/2 bg-gray-200 rounded"></div>
              <div className="h-16 w-full bg-gray-200 rounded"></div>
            </Card>
          ))}
        </div>
      )}

      {/* Queries Over Time Chart */}
      <Card>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Queries Over Time</h2>
        {queriesOverTime && queriesOverTime.length > 0 ? (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={queriesOverTime} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="timestamp" tickFormatter={(tick) => format(parseISO(tick), 'MMM dd')} />
              {/* BEGIN: MODIFIED CODE */}
              {/* Ensure Y-axis displays integer values for query counts */}
              <YAxis allowDecimals={false} tickFormatter={(tick: any) => String(Math.floor(tick))} />
              {/* END: MODIFIED CODE */}
              <Tooltip content={<CustomTooltip unit=" queries" />} />
              <Legend />
              <Line type="monotone" dataKey="queries" name="Total Queries" stroke="#3b82f6" strokeWidth={2} activeDot={{ r: 8 }} />
              <Line type="monotone" dataKey="successful" name="Successful Queries" stroke="#10b981" strokeWidth={2} activeDot={{ r: 8 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            icon={BarChartIcon}
            title="No query data available"
            description="No queries were made during the selected period. Try adjusting the date range."
          />
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Documents */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Most Queried Documents</h2>
          {topDocuments && topDocuments.length > 0 ? (
            // BEGIN: MODIFIED CODE - Replaced custom Table with ResponsiveTable
            <ResponsiveTable columns={topDocumentsColumns} data={topDocuments} keyField="documentId" />
            // END: MODIFIED CODE
          ) : (
            <EmptyState
              icon={Info}
              title="No top documents"
              description="No documents were queried in the selected period."
            />
          )}
        </Card>

        {/* Common Queries */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Most Common Queries</h2>
          {commonQueries && commonQueries.length > 0 ? (
            // BEGIN: MODIFIED CODE - Replaced custom Table with ResponsiveTable
            <ResponsiveTable columns={commonQueriesColumns} data={commonQueries} keyField="query" />
            // END: MODIFIED CODE
          ) : (
            <EmptyState
              icon={Info}
              title="No common queries"
              description="No queries were made during the selected period."
            />
          )}
        </Card>
      </div>

      {/* Performance Metrics Section (Response Time Distribution Bar Chart) */}
      <Card>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Response Time Distribution</h2>
        {performance?.responseTimeDistribution && performance.responseTimeDistribution.length > 0 ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={performance.responseTimeDistribution} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="bucket" />
              <YAxis />
              <Tooltip content={<CustomTooltip unit=" queries" />} />
              <Bar dataKey="count">
                {
                  performance.responseTimeDistribution.map((entry: any, index: number) => {
                    let color = '#10b981'; // <1s green
                    if (entry.bucket === '1-2s') color = '#9FE2BF'; // Light green
                    if (entry.bucket === '2-3s') color = '#f59e0b'; // yellow
                    if (entry.bucket === '3-5s') color = '#f97316'; // orange
                    if (entry.bucket === '>5s') color = '#ef4444'; // red
                    return <Cell key={`cell-${index}`} fill={color} />;
                  })
                }
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyState
            icon={BarChartIcon}
            title="No response time data"
            description="No performance data available for the selected period."
          />
        )}
      </Card>

      {/* User Activity Section */}
      <Card>
        <h2 className="text-xl font-semibold text-gray-900 mb-4">User Activity</h2>
        {/* BEGIN: MODIFIED CODE */}
        {/* Corrected data source from 'performance' to 'userActivity' */}
        {userActivity?.topUsers && userActivity.topUsers.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-lg font-medium text-gray-700">Active users this period: <span className="font-bold text-gray-900">{userActivity.activeUsers}</span></p>
              <p className="text-lg font-medium text-gray-700">New users this period: <span className="font-bold text-gray-900">{userActivity.newUsers}</span></p>
              <h3 className="text-md font-semibold mt-4 mb-2">Top Users</h3>
              {/* BEGIN: MODIFIED CODE - Replaced custom Table with ResponsiveTable */}
              <ResponsiveTable columns={topUsersColumns} data={userActivity.topUsers} keyField="userId" />
              {/* END: MODIFIED CODE */}
            </div>
            <div>
              <h3 className="text-md font-semibold mb-2">User Growth Trend</h3>
              {userActivity.userGrowth && userActivity.userGrowth.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <LineChart data={userActivity.userGrowth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="date" tickFormatter={(tick) => format(parseISO(tick), 'MMM dd')} />
                    <YAxis allowDecimals={false} tickFormatter={(tick: any) => String(Math.floor(tick))}/>
                    <Tooltip content={<CustomTooltip unit=" users" />} />
                    <Line type="monotone" dataKey="count" name="New Users" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <EmptyState
                  icon={BarChartIcon}
                  title="No user growth data"
                  description="No user activity data available for the selected period."
                />
              )}
            </div>
          </div>
        ) : (
          <EmptyState
            icon={Info}
            title="No user activity data"
            description="No user activity data available for the selected period."
          />
        )}
        {/* END: MODIFIED CODE */}
      </Card>


      {/* Export Data Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Analytics Data"
      >
        <div className="space-y-4">
          <p className="text-gray-600">Download analytics data for further analysis.</p>

          <div>
            <label htmlFor="export-period" className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <select
              id="export-period"
              className="form-select border border-gray-300 rounded-lg py-2 px-3 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
              value={exportOptions.period}
              onChange={(e) => setExportOptions({ ...exportOptions, period: e.target.value })}
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="all">All time</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Metrics</label>
            <div className="space-y-2">
              {[
                { value: 'queryVolume', label: 'Query volume and trends' },
                { value: 'responseTimes', label: 'Response times' },
                { value: 'successRates', label: 'Success rates' },
                { value: 'topDocuments', label: 'Top documents' },
                { value: 'commonQueries', label: 'Common queries' },
                { value: 'userActivity', label: 'User activity' },
                { value: 'errorLogs', label: 'Error logs' },
              ].map((metric) => (
                <div key={metric.value} className="flex items-center">
                  <input
                    type="checkbox"
                    id={`metric-${metric.value}`}
                    name="metrics"
                    value={metric.value}
                    checked={exportOptions.metrics.includes(metric.value)}
                    onChange={(e) => {
                      const { value, checked } = e.target;
                      setExportOptions((prev) => ({
                        ...prev,
                        metrics: checked
                          ? [...prev.metrics, value]
                          : prev.metrics.filter((m) => m !== value),
                      }));
                    }}
                    className="h-4 w-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor={`metric-${metric.value}`} className="ml-2 block text-sm text-gray-900">
                    {metric.label}
                  </label>
                </div>
              ))}
            </div>
            <div className="mt-2 space-x-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExportOptions((prev) => ({ ...prev, metrics: ['queryVolume', 'responseTimes', 'successRates', 'topDocuments', 'commonQueries', 'userActivity', 'errorLogs'] }))}
              >
                Select All
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExportOptions((prev) => ({ ...prev, metrics: [] }))}
              >
                Deselect All
              </Button>
            </div>
          </div>

          <div>
            <label htmlFor="export-format" className="block text-sm font-medium text-gray-700 mb-1">Format</label>
            <select
              id="export-format"
              className="form-select border border-gray-300 rounded-lg py-2 px-3 bg-white text-gray-700 shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-full"
              value={exportOptions.format}
              onChange={(e) => setExportOptions({ ...exportOptions, format: e.target.value })}
            >
              <option value="csv">CSV (recommended for Excel)</option>
              <option value="json">JSON (for programmatic use)</option>
            </select>
          </div>

          {/* Preview Section (Placeholder) */}
          <div className="bg-gray-50 p-4 rounded-lg text-sm text-gray-700">
            <h4 className="font-semibold mb-2">Export Preview</h4>
            <p>Estimated file size: Calculating...</p>
            <p>Number of rows: Calculating...</p>
            <p className="mt-2 text-gray-500">Sample data will appear here.</p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button variant="secondary" onClick={() => setShowExportModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleExport} loading={exportLoading} disabled={exportOptions.metrics.length === 0}>
            Export
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default AnalyticsPage;
